import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import './Toast.css';

// Single chokepoint for transient UI feedback. Used by share-link copy,
// form-draft restore, future success/error confirmations. Mount <ToastHost>
// once at the App root, then call useToast() from anywhere.
//
// Stays inside the React tree (no portals, no listeners on window) so it
// composes cleanly with the existing layout and respects focus order.

const ToastCtx = createContext(null);

let nextId = 1;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((message, options = {}) => {
    const id = nextId += 1;
    const tone = options.tone || 'info'; // info | success | error
    const duration = options.duration ?? 4000;
    setToasts((current) => [...current, { id, message, tone }]);
    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }
    return id;
  }, [dismiss]);

  const value = { show, dismiss };

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <ToastHost toasts={toasts} onDismiss={dismiss} />
    </ToastCtx.Provider>
  );
};

const ToastHost = ({ toasts, onDismiss }) => {
  if (!toasts.length) return null;
  return (
    <div className="tn-toast-host" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`tn-toast tn-toast--${t.tone}`}
          role={t.tone === 'error' ? 'alert' : 'status'}
          aria-live={t.tone === 'error' ? 'assertive' : 'polite'}
        >
          <span className="tn-toast__msg">{t.message}</span>
          <button
            type="button"
            className="tn-toast__close"
            aria-label="Dismiss"
            onClick={() => onDismiss(t.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};

// Hook — safe to call even when ToastProvider is missing (returns no-op).
// Lets components that may or may not be mounted under a provider call it
// without crashing during isolated tests or Storybook.
export const useToast = () => {
  const ctx = useContext(ToastCtx);
  return ctx || { show: () => {}, dismiss: () => {} };
};

// Convenience: copy a string to clipboard, then surface a toast.
// Used by share-link buttons, copy-email on Contact, etc.
export const useCopyToClipboard = () => {
  const { show } = useToast();
  return useCallback(async (text, label = 'Copied') => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older Safari / non-secure contexts.
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-1000px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      show(label, { tone: 'success', duration: 2500 });
      return true;
    } catch (_e) {
      show('Could not copy. Press ⌘C / Ctrl+C to copy manually.', { tone: 'error', duration: 4000 });
      return false;
    }
  }, [show]);
};

// Effect helper: show a toast once on mount (used by form-draft restore).
export const useToastOnMount = (shouldShow, message, options) => {
  const { show } = useToast();
  useEffect(() => {
    if (shouldShow) show(message, options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow]);
};

export default ToastProvider;
